import { useCallback, useMemo, useState } from 'react';
import Steps, { Step } from '@antimatterhq/ui/components/steps';
import {
  StepId,
  STEPS,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { UnsavedChangesDialog } from '@/components/dialog/unsavedChanges/UnsavedChangesDialog';
import {
  bronzeHasUnsavedChanges,
  getIsGoldCompleted,
  getIsSilverPreTransformCompleted,
  getIsBronzeCompleted,
  getIsGettingStartedCompleted,
  getIsSourceCompleted,
  gettingStartedHasUnsavedChanges,
  getUnpublishedChanges,
  goldHasUnsavedChanges,
  preTransformHasUnsavedChanges,
  silverHasUnsavedChanges,
  sourceHasUnsavedChanges,
  getIsSilverCompleted,
} from '@/lib/datasource';
import { exhaustiveMatchingGuard } from '@/utils/util';

export const DatasourceSteps = () => {
  const { currentStep, handleGoToStep, formik, dataSource, baseDatasource } =
    useConfigDataSource();
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false);
  const [nextStep, setNextStep] = useState<StepId | null>(null);

  const isSourceCompleted = useMemo(
    () => getIsSourceCompleted(dataSource),
    [dataSource],
  );

  const isGettingStartCompleted = useMemo(
    () => isSourceCompleted && getIsGettingStartedCompleted(dataSource),
    [dataSource, isSourceCompleted],
  );
  const isBronzeCompleted = useMemo(
    () => isGettingStartCompleted && getIsBronzeCompleted(dataSource),
    [dataSource, isGettingStartCompleted],
  );
  const isPreSilverCompleted = useMemo(
    () => isBronzeCompleted && getIsSilverPreTransformCompleted(dataSource),
    [dataSource, isBronzeCompleted],
  );
  const isSilverCompleted = useMemo(
    () => isPreSilverCompleted && getIsSilverCompleted(dataSource),
    [dataSource, isPreSilverCompleted],
  );
  const isGoldCompleted = useMemo(
    () => isSilverCompleted && getIsGoldCompleted(dataSource),
    [dataSource, isSilverCompleted],
  );

  const getCompleted = useCallback(
    (id: StepId) => {
      switch (id) {
        case 'source':
          return isSourceCompleted;
        case 'getting-started':
          return isGettingStartCompleted;
        case 'bronze':
          return isBronzeCompleted;
        case 'silver-pre-transform':
          return isPreSilverCompleted;
        case 'silver':
          return isSilverCompleted;
        case 'gold':
          return isGoldCompleted;
        case 'review':
          return false;
        default:
          return exhaustiveMatchingGuard(id);
      }
    },
    [
      isSourceCompleted,
      isGettingStartCompleted,
      isBronzeCompleted,
      isPreSilverCompleted,
      isSilverCompleted,
      isGoldCompleted,
    ],
  );

  const getClickable = useCallback(
    (id: StepId) => {
      switch (id) {
        case 'source':
          return true;
        case 'getting-started':
          return isSourceCompleted;
        case 'bronze':
          return isGettingStartCompleted;
        case 'silver-pre-transform':
          return isBronzeCompleted;
        case 'silver':
          return isPreSilverCompleted;
        case 'gold':
          return isSilverCompleted;
        case 'review':
          return isGoldCompleted;
        default:
          return exhaustiveMatchingGuard(id);
      }
    },
    [
      isSourceCompleted,
      isGettingStartCompleted,
      isBronzeCompleted,
      isPreSilverCompleted,
      isSilverCompleted,
      isGoldCompleted,
    ],
  );

  const getHasUnappliedChanges = useCallback(
    (id: StepId) => {
      if (!getCompleted(id)) {
        return false;
      }

      const source = getUnpublishedChanges(baseDatasource);
      if (!source) {
        return false;
      }

      switch (id) {
        case 'source':
          return sourceHasUnsavedChanges(baseDatasource, source);
        case 'getting-started':
          return gettingStartedHasUnsavedChanges(baseDatasource, source);
        case 'bronze':
          return bronzeHasUnsavedChanges(baseDatasource, source);
        case 'silver-pre-transform':
          return preTransformHasUnsavedChanges(baseDatasource, source);
        case 'silver':
          return silverHasUnsavedChanges(baseDatasource, source);
        case 'gold':
          return goldHasUnsavedChanges(baseDatasource, source);
        case 'review':
          return false;
        default:
          return exhaustiveMatchingGuard(id);
      }
    },
    [getCompleted, baseDatasource],
  );

  const getStepState = useCallback(
    (id: StepId) => {
      return getHasUnappliedChanges(id) ? 'warning' : 'none';
    },
    [getHasUnappliedChanges],
  );

  const steps = useMemo(
    () =>
      STEPS.map(({ id, name }) => {
        const step: Step = {
          id,
          name,
          completed: getCompleted(id),
          clickable: getClickable(id),
          state: getStepState(id),
        };
        return step;
      }),
    [getCompleted, getClickable, getStepState],
  );

  const onConfirm = useCallback(() => {
    handleGoToStep(nextStep!);
    formik?.handleSubmit();
    setNextStep(null);
    setUnsavedChangesDialogOpen(false);
  }, [formik, nextStep, handleGoToStep]);

  const onDiscard = useCallback(() => {
    handleGoToStep(nextStep!);
    setNextStep(null);
    setUnsavedChangesDialogOpen(false);
  }, [nextStep, handleGoToStep]);

  const onStepClick = useCallback(
    ({ id }: { id: StepId }) => {
      if (!formik?.dirty) {
        handleGoToStep(id);
      } else {
        setNextStep(id);
        setUnsavedChangesDialogOpen(true);
      }
    },
    [formik, handleGoToStep],
  );

  return (
    <>
      <Steps
        steps={steps}
        currentStepId={currentStep}
        handleStepClick={({ id }) => onStepClick({ id: id as StepId })}
        data-testid="configure-datasource-steps"
      />
      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        onDiscard={onDiscard}
        onCancel={() => setUnsavedChangesDialogOpen(false)}
        onConfirm={onConfirm}
      />
    </>
  );
};
